import { useState, useRef, useEffect, useCallback } from "react";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent,
} from "@/components/ui/dialog";
import { Upload, X, Play, Image as ImageIcon, Film, Loader2, Trash2, ZoomIn, FileText, Mail } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTicketMedia, useDeleteTicketMedia } from "@/hooks/use-tickets";

// ── Types ────────────────────────────────────────────────────────────────────

export interface StagedFile {
  file: File;
  previewUrl: string;
  mediaType: "image" | "video";
}

interface MediaItem {
  id: string;
  ticketId: string;
  storagePath: string;
  mediaType: "image" | "video" | "document";
  mimeType: string | null;
  originalFilename: string | null;
  sizeBytes: number | null;
  uploadedBy: string | null;
  createdAt: string;
  signedUrl: string | null;
  source?: string | null;
  emailFrom?: string | null;
}

// ── Constants ────────────────────────────────────────────────────────────────

const ALLOWED_IMAGES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const ALLOWED_VIDEOS = ["video/mp4", "video/quicktime", "video/webm"];
const ALLOWED_DOCS = ["application/pdf"];
const MAX_FILES_PER_TICKET = 30;

// ── Helpers ──────────────────────────────────────────────────────────────────

function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    const url = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Impossible de lire les métadonnées vidéo"));
    };
    video.src = url;
  });
}

async function validateFile(
  file: File,
  toast: ReturnType<typeof useToast>["toast"],
): Promise<{ valid: boolean; mediaType: "image" | "video" | "document" }> {
  const isImage = ALLOWED_IMAGES.includes(file.type);
  const isVideo = ALLOWED_VIDEOS.includes(file.type);
  const isDoc = ALLOWED_DOCS.includes(file.type);

  if (!isImage && !isVideo && !isDoc) {
    toast({
      title: `Type non supporté — ${file.name}`,
      description: `Types acceptés : JPEG, PNG, WEBP, HEIC, MP4, MOV, WEBM, PDF`,
      variant: "destructive",
    });
    return { valid: false, mediaType: "image" };
  }

  if (isImage && file.size > 15 * 1024 * 1024) {
    toast({
      title: `Image trop lourde — ${file.name}`,
      description: "Taille max : 15 Mo",
      variant: "destructive",
    });
    return { valid: false, mediaType: "image" };
  }

  if (isVideo && file.size > 50 * 1024 * 1024) {
    toast({
      title: `Vidéo trop lourde — ${file.name}`,
      description: "Taille max : 50 Mo — réduis la durée ou la qualité",
      variant: "destructive",
    });
    return { valid: false, mediaType: "video" };
  }

  if (isDoc && file.size > 25 * 1024 * 1024) {
    toast({
      title: `PDF trop lourd — ${file.name}`,
      description: "Taille max : 25 Mo",
      variant: "destructive",
    });
    return { valid: false, mediaType: "document" };
  }

  if (isVideo) {
    try {
      const duration = await getVideoDuration(file);
      if (duration > 60) {
        toast({
          title: `Vidéo trop longue — ${file.name}`,
          description: `Durée : ${Math.round(duration)}s — max 1 min`,
          variant: "destructive",
        });
        return { valid: false, mediaType: "video" };
      }
    } catch {
      // Si on ne peut pas lire la durée, on laisse passer
    }
  }

  const mediaType: "image" | "video" | "document" = isVideo ? "video" : isDoc ? "document" : "image";
  return { valid: true, mediaType };
}

// Exported helper — used by parent's handleSubmit for deferred uploads
export async function uploadTicketMedia(ticketId: string, file: File): Promise<void> {
  const isVideo = ALLOWED_VIDEOS.includes(file.type);
  const isDoc = ALLOWED_DOCS.includes(file.type);
  const mediaType = isVideo ? "video" : isDoc ? "document" : "image";

  // 1. Obtenir l'URL signée
  const signRes = await authFetch(`/api/tickets/${ticketId}/media/sign-upload`, {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      mediaType,
    }),
  });
  if (!signRes.ok) {
    const err = await signRes.json().catch(() => ({}));
    throw new Error(err.message || "Erreur URL de signature");
  }
  const { signedUrl, path } = await signRes.json();

  // 2. PUT direct vers Supabase Storage (le fichier ne transite pas par Express)
  const putRes = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!putRes.ok) {
    throw new Error(`Upload Storage échoué (${putRes.status})`);
  }

  // 3. Confirmer la ligne DB
  const confirmRes = await authFetch(`/api/tickets/${ticketId}/media`, {
    method: "POST",
    body: JSON.stringify({
      path,
      mediaType,
      mimeType: file.type,
      originalFilename: file.name,
      sizeBytes: file.size,
    }),
  });
  if (!confirmRes.ok) {
    const err = await confirmRes.json().catch(() => ({}));
    throw new Error(err.message || "Erreur confirmation upload");
  }
}

// ── Dropzone base ─────────────────────────────────────────────────────────────

function DropzoneArea({
  onFiles,
  disabled,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length) onFiles(files);
    },
    [onFiles, disabled],
  );

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
        ${dragging ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:border-primary/50"}
        ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      data-testid="dropzone-media"
    >
      <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
      <p className="text-xs text-muted-foreground">
        Glisser-déposer ou <span className="text-primary underline">parcourir</span>
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5">
        Images · Vidéos (≤ 50 Mo, ≤ 1 min) · PDF (≤ 25 Mo)
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onFiles(files);
          e.target.value = "";
        }}
        data-testid="input-media-file"
      />
    </div>
  );
}

// ── TicketMediaDropzone — mode création (upload différé) ──────────────────────

export function TicketMediaDropzone({
  onFilesChange,
}: {
  onFilesChange: (files: File[]) => void;
}) {
  const { toast } = useToast();
  const [staged, setStaged] = useState<StagedFile[]>([]);

  // Révoquer les URLs blob à l'unmount
  useEffect(() => {
    return () => { staged.forEach((s) => URL.revokeObjectURL(s.previewUrl)); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (staged.length + files.length > MAX_FILES_PER_TICKET) {
        toast({
          title: `Maximum ${MAX_FILES_PER_TICKET} fichiers par ticket`,
          variant: "destructive",
        });
        return;
      }
      const newStaged: StagedFile[] = [];
      for (const file of files) {
        const { valid, mediaType } = await validateFile(file, toast);
        if (!valid) continue;
        newStaged.push({ file, previewUrl: URL.createObjectURL(file), mediaType });
      }
      if (!newStaged.length) return;
      setStaged((prev) => {
        const next = [...prev, ...newStaged];
        onFilesChange(next.map((s) => s.file));
        return next;
      });
    },
    [staged.length, toast, onFilesChange],
  );

  const remove = (idx: number) => {
    setStaged((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      const next = prev.filter((_, i) => i !== idx);
      onFilesChange(next.map((s) => s.file));
      return next;
    });
  };

  return (
    <div className="space-y-2">
      <DropzoneArea onFiles={handleFiles} />
      {staged.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-2">
          {staged.map((s, idx) => (
            <div key={idx} className="relative group rounded overflow-hidden border bg-muted aspect-square">
              {s.mediaType === "image" ? (
                <img src={s.previewUrl} alt={s.file.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-black/80">
                  <Film className="h-6 w-6 text-white/70" />
                </div>
              )}
              <button
                type="button"
                className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => remove(idx)}
                data-testid={`button-remove-staged-${idx}`}
              >
                <X className="h-3 w-3 text-white" />
              </button>
              <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-[9px] text-white px-1 truncate">
                {s.file.name}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TicketMediaTab — galerie + upload immédiat ─────────────────────────────────

export function TicketMediaTab({
  ticketId,
  canManage,
}: {
  ticketId: string;
  canManage: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: mediaList = [], isLoading } = useTicketMedia(ticketId);
  const deleteMedia = useDeleteTicketMedia(ticketId);

  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<MediaItem | null>(null);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if ((mediaList as MediaItem[]).length + files.length > MAX_FILES_PER_TICKET) {
        toast({ title: `Maximum ${MAX_FILES_PER_TICKET} fichiers par ticket`, variant: "destructive" });
        return;
      }
      for (const file of files) {
        const { valid } = await validateFile(file, toast);
        if (!valid) continue;
        const key = `${file.name}-${Date.now()}`;
        setUploading((prev) => ({ ...prev, [key]: true }));
        try {
          await uploadTicketMedia(ticketId, file);
          await queryClient.invalidateQueries({ queryKey: ["/api/tickets/media", ticketId] });
        } catch (e: any) {
          toast({ title: `Erreur upload — ${file.name}`, description: e.message, variant: "destructive" });
        } finally {
          setUploading((prev) => { const n = { ...prev }; delete n[key]; return n; });
        }
      }
    },
    [mediaList, ticketId, toast, queryClient],
  );

  const uploadingCount = Object.keys(uploading).length;
  const items = mediaList as MediaItem[];

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="space-y-2">
          <DropzoneArea onFiles={handleFiles} disabled={uploadingCount > 0} />
          {uploadingCount > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Upload en cours ({uploadingCount} fichier{uploadingCount > 1 ? "s" : ""})…
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
          Aucune pièce jointe attachée à ce ticket
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {items.map((item) => {
            const isPdf = item.mediaType === "document";
            const isEmail = item.source === "email";
            const filename = item.originalFilename || item.storagePath.split("/").pop() || "";

            if (isPdf) {
              // ── Tuile PDF ─────────────────────────────────────────────
              return (
                <div
                  key={item.id}
                  className="relative group rounded overflow-hidden border bg-muted aspect-square flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-muted/80 transition-colors"
                  onClick={() => item.signedUrl && window.open(item.signedUrl, "_blank", "noopener,noreferrer")}
                  data-testid={`media-thumb-${item.id}`}
                >
                  <FileText className="h-8 w-8 text-red-500" />
                  <Badge variant="outline" className="text-[9px] px-1 bg-background/80">PDF</Badge>
                  {/* Badge email */}
                  {isEmail && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="absolute top-1 left-1 bg-blue-500/90 rounded-full p-0.5">
                          <Mail className="h-2.5 w-2.5 text-white" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs max-w-[200px] truncate">
                        {item.emailFrom || "via email"}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {/* Overlay actions */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end justify-end p-1 gap-1 opacity-0 group-hover:opacity-100">
                    {canManage && (
                      <button
                        type="button"
                        className="bg-destructive/80 rounded p-1"
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(item.id); }}
                        data-testid={`button-media-delete-${item.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-white" />
                      </button>
                    )}
                  </div>
                  {/* Filename */}
                  <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-[9px] text-white px-1 truncate opacity-0 group-hover:opacity-100">
                    {filename}
                  </p>
                </div>
              );
            }

            // ── Tuile image / vidéo ───────────────────────────────────
            return (
              <div
                key={item.id}
                className="relative group rounded overflow-hidden border bg-muted aspect-square cursor-pointer"
                onClick={() => setLightbox(item)}
                data-testid={`media-thumb-${item.id}`}
              >
                {item.mediaType === "image" && item.signedUrl ? (
                  <img
                    src={item.signedUrl}
                    alt={filename}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove("hidden");
                    }}
                  />
                ) : null}
                {(item.mediaType === "video" || !item.signedUrl) && (
                  <div className={`w-full h-full flex items-center justify-center bg-black/80 ${item.mediaType === "image" ? "hidden" : ""}`}>
                    {item.mediaType === "video" ? (
                      <>
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <Play className="h-8 w-8 text-white" />
                        </div>
                        <Badge className="absolute top-1 right-1 bg-black/70 text-white text-[9px] px-1">VID</Badge>
                      </>
                    ) : (
                      <ImageIcon className="h-6 w-6 text-white/70" />
                    )}
                  </div>
                )}
                {/* Badge email */}
                {isEmail && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="absolute top-1 left-1 bg-blue-500/90 rounded-full p-0.5">
                        <Mail className="h-2.5 w-2.5 text-white" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs max-w-[200px] truncate">
                      {item.emailFrom || "via email"}
                    </TooltipContent>
                  </Tooltip>
                )}
                {/* Overlay actions */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                  <button
                    type="button"
                    className="bg-black/60 rounded p-1"
                    onClick={(e) => { e.stopPropagation(); setLightbox(item); }}
                    data-testid={`button-media-zoom-${item.id}`}
                  >
                    <ZoomIn className="h-3.5 w-3.5 text-white" />
                  </button>
                  {canManage && (
                    <button
                      type="button"
                      className="bg-destructive/80 rounded p-1"
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(item.id); }}
                      data-testid={`button-media-delete-${item.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-white" />
                    </button>
                  )}
                </div>
                {/* Filename */}
                <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-[9px] text-white px-1 truncate opacity-0 group-hover:opacity-100">
                  {filename}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce média ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le fichier sera supprimé définitivement du stockage. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={async () => {
                if (!deleteConfirmId) return;
                try {
                  await deleteMedia.mutateAsync(deleteConfirmId);
                  toast({ title: "Média supprimé" });
                } catch (e: any) {
                  toast({ title: "Erreur suppression", description: e.message, variant: "destructive" });
                }
                setDeleteConfirmId(null);
              }}
              data-testid="button-confirm-delete-media"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-2 flex items-center justify-center bg-black/90 border-0">
          {lightbox?.mediaType === "image" && lightbox.signedUrl ? (
            <img
              src={lightbox.signedUrl}
              alt={lightbox.originalFilename || "image"}
              className="max-w-full max-h-[85vh] object-contain rounded"
            />
          ) : lightbox?.mediaType === "video" && lightbox.signedUrl ? (
            <video
              src={lightbox.signedUrl}
              controls
              autoPlay
              className="max-w-full max-h-[85vh] rounded"
            />
          ) : (
            <div className="text-white text-sm">Aperçu indisponible</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
